import { z } from 'zod';
import { indirizzoSchema, datiCatastaliSchema, partyBaseSchema, regimeFiscaleSchema, tipologiaContrattoSchema, validateCanoneCoerenza, validateDurata, } from './contract.js';
import { ibanAdeSchema } from './iban.js';
/**
 * Adempimenti successivi (F-004) — proroga, risoluzione, cessione.
 *
 * Un adempimento è un RIFERIMENTO a un contratto già registrato + un piccolo
 * delta per variante; NON un sovrainsieme del contratto. Per questo lo schema
 * NON estende `contractFullBaseSchema`: compone invece i building block esportati
 * da `contract.ts`/`iban.ts` (indirizzo, dati catastali, party, regime fiscale,
 * IBAN AdE) e riusa i validatori puri `validateDurata`/`validateCanoneCoerenza`.
 *
 * Pattern seguiti (INTRODUCTION.md):
 *  - Pattern 1: pre-refinement base + `.superRefine()` + variante `.deepPartial()`
 *    separata per le bozze/autosave (i cross-field non devono fallire su dati
 *    parziali).
 *  - Pattern 2: riuso dei validatori estratti da `contract.ts`, senza forkare la
 *    tabella legale delle durate.
 *
 * Regole non negoziabili (spec/plan):
 *  - `.strict()` su ogni variante (rifiuta chiavi cross-variante).
 *  - Proroga 4+4 ⇒ esattamente +48 mesi; 3+2 ⇒ esattamente +36 mesi
 *    (rinnovo automatico normativo, UC-S04 ext. 4b).
 *  - `prefillFromParent()` non include MAI l'IBAN.
 */
export const adempimentoTypeSchema = z.enum(['PROROGA', 'RISOLUZIONE', 'CESSIONE']);
// ---------------------------------------------------------------------------
// Date-math helpers (pure, nessuna dipendenza da librerie di date esterne — la
// codebase non ne usa).
// ---------------------------------------------------------------------------
/**
 * Numero intero di mesi tra due date (`to - from`), calendario-consapevole:
 * gestisce gli anni e "aggiusta" quando il giorno di `to` è precedente al giorno
 * di `from` nel mese. Utile per verificare l'incremento esatto di una proroga.
 */
function monthsBetween(from, to) {
    let months = (to.getFullYear() - from.getFullYear()) * 12 + (to.getMonth() - from.getMonth());
    if (to.getDate() < from.getDate()) {
        months -= 1;
    }
    return months;
}
/**
 * Data di scadenza naturale = `dataStipula` + `durataMesi` mesi.
 *
 * `dataStipula` accetta anche `string` perché `parentDati` (letto da
 * `Pratica.datiStrutturati`, una colonna Prisma `Json`) non passa mai per lo
 * schema Zod prima di arrivare qui — a runtime, dopo il round-trip JSON reale
 * di Postgres, un campo data è sempre una stringa ISO, mai un'istanza `Date`
 * (JSON non ha un tipo Date nativo). Il cast `as ContractDraft` nei chiamanti
 * (submit.ts) mente al type-checker: `new Date(dataStipula)` normalizza sia
 * l'istanza `Date` sia la stringa ISO allo stesso modo.
 */
function computeScadenza(dataStipula, durataMesi) {
    const scadenza = new Date(dataStipula);
    scadenza.setMonth(scadenza.getMonth() + durataMesi);
    return scadenza;
}
// ---------------------------------------------------------------------------
// Riferimento comune alla pratica madre (condiviso da tutte le varianti).
// ---------------------------------------------------------------------------
const commonRiferimentoSchema = z.object({
    immobile: indirizzoSchema,
    catastali: datiCatastaliSchema,
    locatore: partyBaseSchema,
    regimeFiscale: regimeFiscaleSchema,
    tipologiaContrattoOriginale: tipologiaContrattoSchema,
    dataStipulaOriginale: z.coerce.date(),
    dataScadenzaOriginale: z.coerce.date(),
});
// ---------------------------------------------------------------------------
// Schema per variante (pre-refinement, `.strict()`).
// ---------------------------------------------------------------------------
const prorogaBaseSchema = commonRiferimentoSchema
    .extend({
    adempimentoType: z.literal('PROROGA'),
    conduttore: partyBaseSchema,
    dataInizioProroga: z.coerce.date(),
    dataFineProroga: z.coerce.date(),
    adeguamentoIstat: z.boolean().optional(),
    nuovoCanoneMensile: z.number().positive().optional(),
    nuovoCanoneAnnuale: z.number().positive().optional(),
    ibanAdE: ibanAdeSchema.optional(),
})
    .strict();
const risoluzioneBaseSchema = commonRiferimentoSchema
    .extend({
    adempimentoType: z.literal('RISOLUZIONE'),
    conduttore: partyBaseSchema,
    dataRisoluzione: z.coerce.date(),
    motivo: z.enum(['ACCORDO', 'RECESSO_INQUILINO', 'ALTRO']),
    motivoAltro: z.string().min(1).optional(),
})
    .strict();
const cessioneBaseSchema = commonRiferimentoSchema
    .extend({
    adempimentoType: z.literal('CESSIONE'),
    conduttoreUscente: partyBaseSchema,
    nuovoInquilino: partyBaseSchema,
    dataDecorrenza: z.coerce.date(),
})
    .strict();
// ---------------------------------------------------------------------------
// Cross-field refinements per variante.
// ---------------------------------------------------------------------------
function applyAdempimentoRefinements(data, ctx) {
    if (data.adempimentoType === 'PROROGA') {
        const durataMesi = monthsBetween(data.dataInizioProroga, data.dataFineProroga);
        if (data.tipologiaContrattoOriginale === '4+4' && durataMesi !== 48) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: ['dataFineProroga'],
                message: 'Proroga di un contratto 4+4 deve estendere di esattamente 48 mesi (rinnovo automatico)',
            });
        }
        else if (data.tipologiaContrattoOriginale === '3+2' && durataMesi !== 36) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: ['dataFineProroga'],
                message: 'Proroga di un contratto 3+2 deve estendere di esattamente 36 mesi (rinnovo automatico)',
            });
        }
        else if (data.tipologiaContrattoOriginale !== '4+4' &&
            data.tipologiaContrattoOriginale !== '3+2') {
            validateDurata({ tipologia: data.tipologiaContrattoOriginale, durataMesi }, ctx);
        }
        if (data.nuovoCanoneMensile !== undefined && data.nuovoCanoneAnnuale !== undefined) {
            validateCanoneCoerenza({ canoneMensile: data.nuovoCanoneMensile, canoneAnnuale: data.nuovoCanoneAnnuale }, ctx);
        }
    }
    if (data.adempimentoType === 'RISOLUZIONE') {
        if (data.dataRisoluzione < data.dataStipulaOriginale) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: ['dataRisoluzione'],
                message: 'La data di risoluzione non può essere anteriore alla stipula originaria',
            });
        }
        if (data.dataRisoluzione > data.dataScadenzaOriginale) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: ['dataRisoluzione'],
                message: 'La data di risoluzione non può essere successiva alla scadenza naturale',
            });
        }
        if (data.motivo === 'ALTRO' && !data.motivoAltro?.trim()) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: ['motivoAltro'],
                message: 'Specificare il motivo quando si seleziona "Altro"',
            });
        }
    }
    if (data.adempimentoType === 'CESSIONE') {
        if (data.nuovoInquilino.codiceFiscale === data.conduttoreUscente.codiceFiscale) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: ['nuovoInquilino', 'codiceFiscale'],
                message: 'Il nuovo inquilino non può coincidere con quello uscente',
            });
        }
    }
}
// ---------------------------------------------------------------------------
// Schemi pubblici: full (con refinement) + draft (deepPartial, senza refinement).
// ---------------------------------------------------------------------------
export const adempimentoFullSchema = z
    .discriminatedUnion('adempimentoType', [
    prorogaBaseSchema,
    risoluzioneBaseSchema,
    cessioneBaseSchema,
])
    .superRefine(applyAdempimentoRefinements);
/**
 * Variante "bozza": ogni campo opzionale via `.deepPartial()`, ma il
 * discriminante `adempimentoType` resta un `z.literal(...)` obbligatorio (un
 * discriminated union non può avere il discriminante opzionale). Bypassa
 * intenzionalmente i cross-field refinement — gli autosave parziali non devono
 * fallire validazione (mirror di `contractDraftSchema`).
 */
export const adempimentoDraftSchema = z.discriminatedUnion('adempimentoType', [
    prorogaBaseSchema.deepPartial().extend({ adempimentoType: z.literal('PROROGA') }),
    risoluzioneBaseSchema.deepPartial().extend({ adempimentoType: z.literal('RISOLUZIONE') }),
    cessioneBaseSchema.deepPartial().extend({ adempimentoType: z.literal('CESSIONE') }),
]);
// ---------------------------------------------------------------------------
// Prefill deterministico dai dati della pratica madre.
// ---------------------------------------------------------------------------
/**
 * Calcola i campi comuni pre-compilabili di un adempimento a partire dai dati
 * strutturati della pratica madre (shape `ContractDraft`).
 *
 * Deterministico, nessun I/O. Allowlist esplicita — nessuno spread cieco di
 * `parentDati`. L'IBAN NON viene MAI copiato (Non-Functional: prefill mai
 * include IBAN) e non compare né in `data` né in `prefilledPaths`.
 *
 * - PROROGA / RISOLUZIONE: `conduttore` pre-compilato dal conduttore madre.
 * - CESSIONE: `conduttoreUscente` pre-compilato read-only; `nuovoInquilino`
 *   volutamente NON pre-compilato.
 */
/**
 * Nomi dei campi comuni immutabili di un adempimento (lato adempimento). Sono
 * un RIFERIMENTO alla pratica madre e non devono MAI essere fidati dall'eco del
 * client: al submit vanno ri-derivati server-side dalla madre verificata.
 *
 * NB: questi nomi (`tipologiaContrattoOriginale`, `dataStipulaOriginale`,
 * `dataScadenzaOriginale`) NON coincidono con i nomi lato pratica madre
 * (`tipologia`, `dataStipula`, `durataMesi`) — per questo la ri-derivazione
 * richiede il mapping esplicito di `rederiveImmutableFromParent()` e non una
 * copia per chiave identica.
 */
export const IMMUTABLE_ADEMPIMENTO_FIELDS = [
    'immobile',
    'catastali',
    'locatore',
    'regimeFiscale',
    'tipologiaContrattoOriginale',
    'dataStipulaOriginale',
    'dataScadenzaOriginale',
];
/**
 * Ri-deriva i campi comuni immutabili di un adempimento dai dati strutturati
 * della pratica madre (shape `ContractDraft`), con lo STESSO mapping esplicito
 * usato da `prefillFromParent()` — così il path di prefill (client) e il path di
 * ri-derivazione difensiva (server, al submit) non possono divergere.
 *
 * Mapping (lato-madre → lato-adempimento):
 *  - immobile / catastali / locatore / regimeFiscale → stesso nome
 *  - tipologiaContrattoOriginale ← parentDati.tipologia
 *  - dataStipulaOriginale        ← parentDati.dataStipula
 *  - dataScadenzaOriginale       ← computeScadenza(dataStipula, durataMesi)
 *
 * Deterministico, nessun I/O. Ritorna solo le chiavi effettivamente presenti
 * sulla madre (le assenti non vengono forzate a `undefined`), così il chiamante
 * può fondere il risultato SOPRA il payload del client sovrascrivendo solo i
 * campi realmente ri-derivabili. L'IBAN NON viene MAI copiato.
 */
export function rederiveImmutableFromParent(parentDati) {
    const out = {};
    if (parentDati.immobile !== undefined)
        out.immobile = parentDati.immobile;
    if (parentDati.catastali !== undefined)
        out.catastali = parentDati.catastali;
    if (parentDati.locatore !== undefined)
        out.locatore = parentDati.locatore;
    if (parentDati.regimeFiscale !== undefined) {
        out.regimeFiscale = parentDati.regimeFiscale;
    }
    // Mapping esplicito nome-madre → nome-adempimento (il punto del fix TR-001).
    if (parentDati.tipologia !== undefined) {
        out.tipologiaContrattoOriginale = parentDati.tipologia;
    }
    if (parentDati.dataStipula !== undefined) {
        out.dataStipulaOriginale = parentDati.dataStipula;
    }
    if (parentDati.dataStipula !== undefined && parentDati.durataMesi !== undefined) {
        out.dataScadenzaOriginale = computeScadenza(parentDati.dataStipula, parentDati.durataMesi);
    }
    return out;
    // NEVER copy ibanAdE from parent (Non-Functional: prefill never includes IBAN).
}
export function prefillFromParent(parentDati, tipo) {
    const dataScadenzaOriginale = parentDati.dataStipula !== undefined && parentDati.durataMesi !== undefined
        ? computeScadenza(parentDati.dataStipula, parentDati.durataMesi)
        : undefined;
    const common = {
        immobile: parentDati.immobile,
        catastali: parentDati.catastali,
        locatore: parentDati.locatore,
        regimeFiscale: parentDati.regimeFiscale,
        tipologiaContrattoOriginale: parentDati.tipologia,
        dataStipulaOriginale: parentDati.dataStipula,
        dataScadenzaOriginale,
    };
    const prefilledPaths = [
        'immobile',
        'catastali',
        'locatore',
        'regimeFiscale',
        'tipologiaContrattoOriginale',
        'dataStipulaOriginale',
        'dataScadenzaOriginale',
    ];
    if (tipo === 'PROROGA' || tipo === 'RISOLUZIONE') {
        return {
            data: {
                ...common,
                adempimentoType: tipo,
                conduttore: parentDati.conduttore,
            },
            prefilledPaths: [...prefilledPaths, 'conduttore'],
        };
    }
    // CESSIONE: conduttoreUscente pre-compilato read-only; nuovoInquilino NON pre-compilato.
    return {
        data: {
            ...common,
            adempimentoType: tipo,
            conduttoreUscente: parentDati.conduttore,
        },
        prefilledPaths: [...prefilledPaths, 'conduttoreUscente'],
    };
    // NEVER copy ibanAdE from parent (Non-Functional: prefill never includes IBAN).
}
//# sourceMappingURL=adempimento.js.map